import { signalStore, withState, withMethods, patchState } from '@ngrx/signals';
import { GeoJSON, Map, Layer } from 'leaflet';
import { Indicator } from '../../models/indicator';
import { Colorset } from '../../models/colorset';
import { DataEntry, IndicatorData } from '../../models/indicator-data';
import { Scale } from '../../models/scale';
import { WidgetConfig } from '../../services/grid-widget-config.service';
import {
  removeAllEntities,
  removeEntity,
  setEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { IndicatorLegend } from '../../services/map/map-legend.service';

export interface IdentifiableLayer extends Layer {
  id: string;
  scales?: Scale[];
  indicator?: Indicator;
}

export interface MapState {
  _id: string;
  map: Map | null;
  geoJsonLayer: GeoJSON | null;
  tileLayer: Layer | null;
  tileLayerType: string | undefined;
  scales: Scale[] | null;
  indicator: Indicator | null;
  indicatorData: IndicatorData | null;
  data: DataEntry | null;
  comparisonValues: any | null;
  colors: string[];
  colorset: Colorset | null;
  config: WidgetConfig | null;
  featureHighlight: any | null;
  mouseOverFeatureKey: string | null;
  indicatorLegends: Record<string, IndicatorLegend>;
}

export const initialState: MapState = {
  _id: '',
  map: null,
  geoJsonLayer: null,
  tileLayer: null,
  tileLayerType: undefined,
  indicator: null,
  indicatorData: null,
  colorset: null,
  featureHighlight: null,
  mouseOverFeatureKey: null,
  data: null,
  comparisonValues: null,
  scales: null,
  colors: [],
  config: null,
  indicatorLegends: {},
};

/**
 * MapStore - Pure state management for map components
 *
 * This store only manages state - no business logic, rendering, or UI creation.
 * Use the following services for additional functionality:
 * - MapRenderService: Rendering and styling
 * - MapLegendService: Legend creation and management
 * - MapEventService: Event handling
 * - MapTooltipService: Tooltip and popup generation
 * - MapDataHelper: Data transformation and color management
 */
export const MapStore = signalStore(
  withEntities<IdentifiableLayer>(),
  withState(initialState),
  withMethods(store => ({
    // Map instance management
    setMap(map: Map, mapContainerId: string): void {
      patchState(store, { map, _id: mapContainerId });
    },

    // Layer management
    setGeoJsonLayer(layer: GeoJSON | null): void {
      patchState(store, { geoJsonLayer: layer });
    },

    setTileLayer(layer: Layer | null, layerType?: string): void {
      patchState(store, { tileLayer: layer, tileLayerType: layerType });
    },

    addLayer(layer: IdentifiableLayer): void {
      patchState(store, setEntity(layer));
    },

    removeLayer(layerId: string): void {
      patchState(store, removeEntity(layerId));
    },

    removeAllLayers(): void {
      patchState(store, removeAllEntities());
    },

    getLayer(layerId: string): IdentifiableLayer | undefined {
      return store.entityMap()[layerId];
    },

    // Legend management
    addIndicatorLegend(indicatorId: string, legend: IndicatorLegend): void {
      const legends = store.indicatorLegends();
      patchState(store, {
        indicatorLegends: { ...legends, [indicatorId]: legend },
      });
    },

    removeIndicatorLegend(indicatorId: string): void {
      const legends = { ...store.indicatorLegends() };
      delete legends[indicatorId];
      patchState(store, { indicatorLegends: legends });
    },

    getIndicatorLegend(indicatorId: string): IndicatorLegend | undefined {
      return store.indicatorLegends()[indicatorId];
    },

    // State updates
    setConfig(config: WidgetConfig): void {
      patchState(store, { config });
    },

    setIndicator(indicator: Indicator | null): void {
      patchState(store, { indicator });
    },

    setIndicatorData(indicatorData: IndicatorData | null): void {
      patchState(store, { indicatorData });
    },

    setColors(colors: string[], colorset: Colorset | null): void {
      patchState(store, { colors, colorset });
    },

    setScales(scales: Scale[] | null): void {
      patchState(store, { scales });
    },

    setData(data: DataEntry | null): void {
      patchState(store, { data });
    },

    setComparisonValues(comparisonValues: any | null): void {
      patchState(store, { comparisonValues });
    },

    // Feature highlight management
    setFeatureHighlight(featureHighlight: any | null): void {
      patchState(store, { featureHighlight });
    },

    setMouseOverFeatureKey(key: string | null): void {
      patchState(store, { mouseOverFeatureKey: key });
    },

    // Batch state update
    updateMapState(updates: Partial<MapState>): void {
      patchState(store, updates);
    },

    // Reset store
    reset(): void {
      patchState(store, initialState);
    },
  }))
);
